import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { B100 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import {
	type AdditionalCreateIssueContext,
	type CreateIssueReturn,
	InlineCreateForm,
	ROW_APPEARANCE,
} from '@atlassian/jira-business-issue-create';
import type { ChildrenQueryData } from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import { INSIDE, BEFORE, LAST } from '@atlassian/jira-software-roadmap-timeline-table';
import type { Item } from '@atlassian/jira-software-roadmap-timeline-table/src/types';
import { fieldIds } from '../../../common/constants';
import { useCreateItemAnchor } from '../../../controllers/create-item-anchor';
import { useOptimisticIssueCreate } from '../../../controllers/use-optimistic-issue-create';
import type { Issue } from '../../../services/create-issue';
import type {
	TimelineViewIssue,
	TimelineViewIssuesReturn,
} from '../../../services/get-timeline-view-issues/types';

type Props = {
	childrenQueryData: ChildrenQueryData<TimelineViewIssuesReturn>;
	issuesMap: Map<string, TimelineViewIssue>;
	items: Item[];
};

export const TimelineTableInlineCreate = ({ childrenQueryData, issuesMap, items }: Props) => {
	const [{ createItemAnchor }, { setCreateItemAnchor }] = useCreateItemAnchor();
	const { addOptimisticIssue, updateIssue, removeOptimisticIssue } = useOptimisticIssueCreate();

	const onPrepareIssueContext = useCallback((): AdditionalCreateIssueContext => {
		const issueContext: AdditionalCreateIssueContext = {};

		if (createItemAnchor) {
			// if created in between issues
			if (createItemAnchor.position === BEFORE) {
				issueContext.parentIssueId = createItemAnchor.parentId;

				const issueForBeforeRank = createItemAnchor.beforeId
					? issuesMap.get(createItemAnchor.beforeId)
					: null;

				if (issueForBeforeRank?.issueId && issueForBeforeRank?.rank) {
					issueContext.rankBefore = {
						issueId: issueForBeforeRank.issueId,
						rank: issueForBeforeRank.rank,
					};
				}
			}
			// if created at the bottom of the list
			else if (createItemAnchor.position === LAST) {
				const lastIssueId = items[items.length - 1]?.id;
				const lastIssue = issuesMap.get(lastIssueId);

				if (lastIssue?.issueId && lastIssue?.rank) {
					issueContext.rankAfter = {
						issueId: lastIssue.issueId,
						rank: lastIssue.rank,
					};
				}
			}
			// if created as a child of an issue
			else if (createItemAnchor.position === INSIDE) {
				const parentIssue = items.find((i) => i.id === createItemAnchor.parentId);
				const childItems = parentIssue?.childItems ?? [];
				// this can be undefined for parent without children
				const lastChildWithinParent =
					childItems.length > 0 ? childItems[childItems.length - 1].id : null;

				const lastChildWithinParentIssue = lastChildWithinParent
					? issuesMap.get(lastChildWithinParent)
					: null;

				issueContext.parentIssueId = createItemAnchor.parentId;

				if (lastChildWithinParentIssue?.issueId && lastChildWithinParentIssue?.rank) {
					issueContext.rankAfter = {
						issueId: lastChildWithinParentIssue.issueId,
						rank: lastChildWithinParentIssue.rank,
					};
				}
			}
		}

		return issueContext;
	}, [createItemAnchor, issuesMap, items]);

	const onSubmit = useCallback(
		async ({ optimisticIssuePayload, promise }: CreateIssueReturn<Issue>) => {
			addOptimisticIssue(optimisticIssuePayload, optimisticIssuePayload.parentIssueId);

			try {
				const issue = await promise;
				if (issue) {
					updateIssue(issue, optimisticIssuePayload.parentIssueId);
				} else {
					removeOptimisticIssue(
						optimisticIssuePayload.issueId,
						optimisticIssuePayload.parentIssueId,
					);
				}
			} catch {
				removeOptimisticIssue(optimisticIssuePayload.issueId, optimisticIssuePayload.parentIssueId);
			}
		},
		[addOptimisticIssue, updateIssue, removeOptimisticIssue],
	);

	// Hide the inline create when the user cancels/blurs the form
	const onBlur = useCallback(() => {
		setCreateItemAnchor(undefined);
	}, [setCreateItemAnchor]);

	const parentId =
		createItemAnchor &&
		(createItemAnchor.position === INSIDE || createItemAnchor.position === BEFORE)
			? createItemAnchor.parentId
			: undefined;

	const hierarchyLevels = useMemo(() => {
		if (ff('jwm.visual-hierarchy-milestone-1')) {
			if (parentId) {
				const parent = issuesMap.get(parentId);
				const parentHierarchyLevel = parent?.issueType.hierarchyLevel;
				return parentHierarchyLevel !== undefined
					? {
							maxHierarchyLevel: parentHierarchyLevel - 1,
							minHierarchyLevel: parentHierarchyLevel - 1,
						}
					: undefined;
			}

			return undefined;
		}

		return parentId ? { maxHierarchyLevel: -1, minHierarchyLevel: -1 } : undefined;
	}, [issuesMap, parentId]);

	const queryData = parentId ? childrenQueryData[parentId] : undefined;

	if (parentId && (!queryData || queryData.loading)) {
		if (!queryData) {
			return null;
		}

		return (
			<InlineCreateLoadingWrapper>
				<SpinnerWrapper>
					<Spinner size="small" />
				</SpinnerWrapper>
				<InlineCreateLoading type="text" disabled />
			</InlineCreateLoadingWrapper>
		);
	}

	return (
		<Box xcss={inlineCreateWrapperStyles}>
			<InlineCreateForm
				appearance={ROW_APPEARANCE}
				location={createItemAnchor?.position === BEFORE ? 'timelineSibling' : 'timelineHierarchy'}
				fieldIdsToReturn={fieldIds}
				isCompact
				onBlur={onBlur}
				onPrepareIssueContext={onPrepareIssueContext}
				onSubmit={onSubmit}
				{...hierarchyLevels}
			/>
		</Box>
	);
};

const inlineCreateWrapperStyles = xcss({
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineCreateLoadingWrapper = styled.div({
	position: 'relative',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.div({
	position: 'absolute',
	top: token('space.050', '4px'),
	left: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineCreateLoading = styled.input({
	width: 'calc(100% - 8px)',
	borderRadius: '2px',
	height: '24px',
	boxShadow: 'none',
	border: `2px solid ${token('color.border.information', B100)}`,
	cursor: 'not-allowed',
});
